import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, of, shareReplay } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import {
  VERSION,
  PROJECT,
  WORKFLOW,
  SNAPSHOT,
  API_BASE,
  FILES,
} from '../../constants/general.constants';
import { AppStateService } from '../app-state/app-state.service';

@Injectable({
  providedIn: 'root',
})
export class SnapshotService {
  private readonly http = inject(HttpClient);

  readonly #appState = inject(AppStateService);

  // ✅ Cache for snapshots to prevent duplicate API calls
  private snapshotsCache: Map<string, { data: any; timestamp: number; request$: Observable<any> | null }> = new Map();
  private readonly CACHE_DURATION = 5000;

  // Create a snapshot for a workflow
  createSnapshot(data: {
    name: string;
    cacheId: number;
    content: any;
  }): Observable<any> {
    const projectId = this.#appState.projectId();
    const versionId = this.#appState.versionId();
    const workflowId = this.#appState.getCurrentWorkflowOrNotebookId();

    return this.http.post(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/${WORKFLOW}/${workflowId}/${SNAPSHOT}`,
      data
    ).pipe(
      tap(() => {
        // Invalidate cache after creating a snapshot
        const cacheKey = `${projectId}_${versionId}_${workflowId}`;
        this.snapshotsCache.delete(cacheKey);
      })
    );
  }

  // Get a list of snapshots for a workflow with caching
  getSnapshots(): Observable<any> {
    const projectId = this.#appState.projectId();
    const versionId = this.#appState.versionId();
    const workflowId = this.#appState.getCurrentWorkflowOrNotebookId();

    const cacheKey = `${projectId}_${versionId}_${workflowId}`;
    const cached = this.snapshotsCache.get(cacheKey);

    // Return cached data if still valid
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      if (cached.data) {
        console.log('📦 SnapshotService: Returning cached snapshots');
        return of(cached.data);
      }
      // If there's a pending request, reuse it
      // With refCount: false, the observable stays alive even if all subscribers unsubscribe
      if (cached.request$) {
        console.log('⏳ SnapshotService: Reusing pending snapshots request');
        return cached.request$;
      }
    }

    // Make a new request
    console.log('🌐 SnapshotService: Making new API request for snapshots');
    const request$ = this.http.get(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/${WORKFLOW}/${workflowId}/${SNAPSHOT}`
    ).pipe(
      tap(data => {
        // Cache the result
        this.snapshotsCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
          request$: null
        });
        console.log('✅ SnapshotService: Snapshots cached');
      }),
      catchError(error => {
        // Clear cache on error to allow retry
        console.error('❌ SnapshotService: Error fetching snapshots', error);
        this.snapshotsCache.delete(cacheKey);
        throw error;
      }),
      // Use refCount: false to keep subscription alive even when all subscribers unsubscribe
      // This prevents ObjectUnsubscribedError when components unsubscribe during version changes
      shareReplay({ bufferSize: 1, refCount: false })
    );

    // Store the pending request
    this.snapshotsCache.set(cacheKey, {
      data: null,
      timestamp: Date.now(),
      request$
    });

    return request$;
  }

  // Get details of a specific snapshot
  getSnapshot(snapshotId: number): Observable<any> {
    const projectId = this.#appState.projectId();
    const versionId = this.#appState.versionId();
    const workflowId = this.#appState.getCurrentWorkflowOrNotebookId();

    if (!!snapshotId === false || snapshotId === 0) {
      return EMPTY;
    }

    return this.http.get(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/${WORKFLOW}/${workflowId}/${SNAPSHOT}/${snapshotId}`
    );
  }

  // Update a specific snapshot
  updateSnapshot(snapshotId: number, data: { name: string; content: string }): Observable<any> {
    const projectId = this.#appState.projectId();
    const versionId = this.#appState.versionId();
    const workflowId = this.#appState.getCurrentWorkflowOrNotebookId();

    if (!!snapshotId === false || snapshotId === 0) {
      return EMPTY;
    }

    return this.http.put(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/${WORKFLOW}/${workflowId}/${SNAPSHOT}/${snapshotId}`,
      data
    );
  }

  // Delete a specific snapshot
  deleteSnapshot(snapshotId: number): Observable<any> {
    const projectId = this.#appState.projectId();
    const versionId = this.#appState.versionId();
    const workflowId = this.#appState.getCurrentWorkflowOrNotebookId();

    if (!!snapshotId === false || snapshotId === 0) {
      return EMPTY;
    }

    return this.http.delete(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/${WORKFLOW}/${workflowId}/${SNAPSHOT}/${snapshotId}`
    ).pipe(
      tap(() => {
        // Invalidate cache after deleting a snapshot
        const cacheKey = `${projectId}_${versionId}_${workflowId}`;
        this.snapshotsCache.delete(cacheKey);
      })
    );
  }

  // Get files associated with a snapshot
  getSnapshotFile(
    snapshotId: number,
    fileId: string,
    responseType: 'text' | 'blob' | 'arraybuffer' | 'document' | 'json' = 'text'
  ): Observable<any> {
    const projectId = this.#appState.projectId();
    const versionId = this.#appState.versionId();
    const workflowId = this.#appState.getCurrentWorkflowOrNotebookId();

    return this.http.get(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/${WORKFLOW}/${workflowId}/${SNAPSHOT}/${snapshotId}/${FILES}/${fileId}`,
      { responseType } as any
    );
  }

  // New method to get snapshot ID from the current URL
  getSnapshotIdFromUrl(): number | null {
    const url = new URL(window.location.href);
    const snapshotId = url.searchParams.get('snapshotId');
    return snapshotId ? parseInt(snapshotId, 10) : null;
  }
}
